<template>
  <v-container fluid>
    <base-material-card
      icon="mdi-clipboard-text"
      title="ข้อมูลรายงานอัตรากำลังของวิทยาลัย แยกตามประเภทวิชา"
      class="px-5 py-3"
    >
      <!-- Enhanced Filter Section -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-expansion-panels v-model="filterPanel" flat>
            <v-expansion-panel class="filter-panel">
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="primary">mdi-filter-variant</v-icon>
                  <span class="font-weight-bold">ตัวกรองข้อมูล</span>
                  <v-spacer></v-spacer>
                  <v-chip 
                    v-if="hasActiveFilters" 
                    color="primary" 
                    small 
                    outlined
                    class="mr-2"
                  >
                    {{ activeFiltersCount }} ตัวกรอง
                  </v-chip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="filters.searchText"
                      label="ค้นหาประเภทวิชา"
                      prepend-inner-icon="mdi-magnify"
                      outlined
                      dense
                      clearable
                      hide-details
                      class="filter-field"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="filters.minPersonnel"
                      :items="personnelRanges"
                      label="จำนวนบุคลากรขั้นต่ำ"
                      outlined
                      dense
                      clearable
                      hide-details
                      class="filter-field"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="filters.chartType"
                      :items="chartTypes"
                      label="ประเภทแผนภูมิ"
                      outlined
                      dense
                      hide-details
                      class="filter-field"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="filters.sortBy"
                      :items="sortOptions"
                      label="เรียงลำดับ"
                      outlined
                      dense
                      hide-details
                      class="filter-field"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-btn
                      color="primary"
                      small
                      @click="applyFilters"
                      class="mr-2"
                    >
                      <v-icon left small>mdi-filter</v-icon>
                      ใช้ตัวกรอง
                    </v-btn>
                    <v-btn
                      color="secondary"
                      small
                      outlined
                      @click="resetFilters"
                    >
                      <v-icon left small>mdi-refresh</v-icon>
                      รีเซ็ต
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Enhanced Summary Cards -->
      <v-row class="mb-4">
        <v-col cols="12" md="4" v-for="(summary, index) in summaryCards" :key="index">
          <v-card 
            :color="summary.color" 
            dark 
            class="summary-card pa-4"
            elevation="6"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <h3 class="text-h4 font-weight-bold mb-1">{{ summary.value.toLocaleString() }}</h3>
                <p class="text-body-1 mb-1">{{ summary.title }}</p>
                <p class="text-caption opacity-80">{{ summary.percentage }}% ของทั้งหมด</p>
              </div>
              <div class="text-right">
                <v-icon size="48" class="opacity-60">{{ summary.icon }}</v-icon>
                <div class="mt-2">
                  <v-chip
                    :color="summary.trendColor"
                    x-small
                    outlined
                    class="trend-chip"
                  >
                    <v-icon x-small left>{{ summary.trendIcon }}</v-icon>
                    {{ summary.trend }}
                  </v-chip>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Enhanced Chart Section -->
      <v-row no-gutters class="mb-4">
        <v-col cols="12">
          <v-card elevation="4" class="chart-card">
            <v-card-title class="chart-header pa-4">
              <v-icon class="mr-2">mdi-chart-bar</v-icon>
              กราฟแสดงจำนวนบุคลากรแยกตามประเภทวิชา
              <v-spacer></v-spacer>
              <v-btn-toggle
                v-model="chartViewMode"
                dense
                color="primary"
                class="chart-toggle"
              >
                <v-btn small value="count">
                  <v-icon small>mdi-counter</v-icon>
                  จำนวน
                </v-btn>
                <v-btn small value="percentage">
                  <v-icon small>mdi-percent</v-icon>
                  เปอร์เซ็นต์
                </v-btn>
              </v-btn-toggle>
              <v-btn
                icon
                small
                @click="refreshData"
                class="ml-2"
                :loading="loading"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="chart-container">
                <div v-if="loading" class="chart-loading">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                  ></v-progress-circular>
                  <p class="mt-3">กำลังโหลดข้อมูล...</p>
                </div>
                <div v-else-if="filteredData.length === 0" class="no-data">
                  <v-icon size="64" color="grey">mdi-chart-bar</v-icon>
                  <p class="mt-3">ไม่พบข้อมูลตามเงื่อนไขที่กำหนด</p>
                </div>
                <apexchart
                  v-else
                  :type="currentChartType"
                  height="450"
                  :options="chartOptions"
                  :series="series"
                ></apexchart>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Enhanced Data Table Section -->
      <v-card elevation="4" class="table-card">
        <v-card-title class="table-header pa-4">
          <v-icon class="mr-2">mdi-table</v-icon>
          ตารางข้อมูลรายละเอียด
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            outlined
            @click="exportData"
            class="export-btn"
          >
            <v-icon left small>mdi-file-excel</v-icon>
            ส่งออกข้อมูล
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row class="mb-3">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="search"
                pend-icon="mdi-magnify"
                label="ค้นหา ระบุคำ หรือ ส่วนข้อความเกี่ยวข้อง"
                single-line
                hide-details
                dense
                filled
                clearable
                class="search-field"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="tableOptions.itemsPerPage"
                :items="[10, 25, 50, 100, -1]"
                :item-text="item => item === -1 ? 'ทั้งหมด' : item + ' รายการ'"
                :item-value="item => item"
                label="จำนวนรายการต่อหน้า"
                dense
                filled
                hide-details
                class="items-per-page-select"
              />
            </v-col>
          </v-row>
          
          <v-data-table
            :loading="loading"
            :headers="headers"
            :items="filteredData"
            :search="search"
            :items-per-page="tableOptions.itemsPerPage"
            :sort-by="tableOptions.sortBy"
            :sort-desc="tableOptions.sortDesc"
            class="enhanced-table"
            :loading-text="'กำลังโหลดข้อมูล...'"
            :no-data-text="'ไม่มีข้อมูลให้แสดง'"
            :no-results-text="'ไม่พบผลลัพธ์ที่ค้นหา'"
          >
            <template v-slot:loading>
              <v-row justify="center" class="pa-4">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                ></v-progress-circular>
              </v-row>
            </template>
            
            <template v-slot:item.countPersonSubjectTeach="{ item }">
              <v-chip
                :color="getChipColor(item.countPersonSubjectTeach, 'total')"
                text-color="white"
                small
                class="font-weight-bold chip-animate"
              >
                {{ item.countPersonSubjectTeach }} คน
              </v-chip>
            </template>
            
            <template v-slot:item.countPersonSubjectTeachGov="{ item }">
              <v-chip
                :color="getChipColor(item.countPersonSubjectTeachGov, 'gov')"
                text-color="white"
                small
                class="font-weight-bold chip-animate"
              >
                {{ item.countPersonSubjectTeachGov }} คน
              </v-chip>
            </template>
            
            <template v-slot:item.countPersonSubjectTeachTem="{ item }">
              <v-chip
                :color="getChipColor(item.countPersonSubjectTeachTem, 'temp')"
                text-color="white"
                small
                class="font-weight-bold chip-animate"
              >
                {{ item.countPersonSubjectTeachTem }} คน
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    @click="viewDetails(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>ดูรายละเอียด</span>
              </v-tooltip>
            </template>

            <template v-slot:no-data>
              <v-alert
                type="info"
                class="ma-4"
                outlined
                icon="mdi-information"
              >
                ไม่มีข้อมูลให้แสดง
              </v-alert>
            </template>

            <template v-slot:no-results>
              <v-alert
                type="error"
                class="ma-4"
                outlined
                icon="mdi-alert"
              >
                ไม่พบผลลัพธ์ "{{ search }}" ที่คุณกำลังค้นหา
              </v-alert>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Statistics Summary -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card elevation="2" class="stats-summary">
            <v-card-title class="stats-header">
              <v-icon class="mr-2">mdi-chart-line</v-icon>
              สถิติสรุป
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <div class="stat-item">
                    <div class="stat-value">{{ statistics.totalSubjects }}</div>
                    <div class="stat-label">ประเภทวิชาทั้งหมด</div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="stat-item">
                    <div class="stat-value">{{ statistics.avgPerSubject }}</div>
                    <div class="stat-label">เฉลี่ยต่อประเภท</div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="stat-item">
                    <div class="stat-value">{{ statistics.maxSubject.name }}</div>
                    <div class="stat-label">สูงสุด ({{ statistics.maxSubject.value }} คน)</div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="stat-item">
                    <div class="stat-value">{{ statistics.minSubject.name }}</div>
                    <div class="stat-label">ต่ำสุด ({{ statistics.minSubject.value }} คน)</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </base-material-card>
  </v-container> 
</template>

<script>
import VueMoment from "vue-moment";
import moment from "moment-timezone";

export default {
  name: "personnel_etc",
  data() {
    return {
      VueMoment,
      moment,
      loading: false,
      ApiKey: "HRvec2021",
      valid: true,
      personnel_etcs: [],
      search: "",
      pagination: {},
      filterPanel: 0,
      chartViewMode: "count",
      
      // Filter states
      filters: {
        searchText: "",
        minPersonnel: null,
        chartType: "bar",
        sortBy: "total_desc"
      },
      
      // Filter options
      personnelRanges: [
        { text: "ไม่จำกัด", value: null },
        { text: "มากกว่า 5 คน", value: 5 },
        { text: "มากกว่า 10 คน", value: 10 },
        { text: "มากกว่า 20 คน", value: 20 },
        { text: "มากกว่า 50 คน", value: 50 }
      ],
      
      chartTypes: [
        { text: "แผนภูมิแท่ง", value: "bar" },
        { text: "แผนภูมิเส้น", value: "line" },
        { text: "แผนภูมิพื้นที่", value: "area" }
      ],
      
      sortOptions: [
        { text: "รวมทั้งหมด (มาก-น้อย)", value: "total_desc" },
        { text: "รวมทั้งหมด (น้อย-มาก)", value: "total_asc" },
        { text: "พนักงานราชการ (มาก-น้อย)", value: "gov_desc" },
        { text: "ครูอัตราจ้าง (มาก-น้อย)", value: "temp_desc" },
        { text: "ชื่อประเภทวิชา (A-Z)", value: "name_asc" }
      ],
      
      tableOptions: {
        itemsPerPage: 25,
        sortBy: "countPersonSubjectTeach",
        sortDesc: true
      },
      
      headers: [
        {
          text: "รหัสประเภทวิชา",
          align: "center",
          value: "id_type_course",
          width: "150px",
          class: "font-weight-bold"
        },
        { 
          text: "ประเภทวิชา", 
          align: "left", 
          value: "subject_typeName",
          class: "font-weight-bold"
        },
        {
          text: "ข้าราชการครู",
          align: "center",
          value: "countPersonSubjectTeach",
          width: "150px",
          class: "font-weight-bold"
        },
        {
          text: "พนักงานราชการ",
          align: "center",
          value: "countPersonSubjectTeachGov",
          width: "150px",
          class: "font-weight-bold"
        },
        {
          text: "ครูอัตราจ้าง",
          align: "center",
          value: "countPersonSubjectTeachTem",
          width: "150px",
          class: "font-weight-bold"
        },
        {
          text: "จัดการ",
          align: "center",
          value: "actions",
          width: "100px",
          sortable: false
        }
      ],
      
      series: [
        {
          name: 'จำนวน',
          data: [],
        },
      ],
      
      baseChartOptions: {
        chart: {
          type: 'bar',
          fontFamily: 'Roboto, sans-serif',
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        colors: ['#4CAF50', '#2196F3', '#FF9800'],
        stroke: {
          curve: 'smooth',
          width: 2
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: '12px',
              fontWeight: 500
            }
          }
        },
        tooltip: {
          theme: 'dark',
          style: {
            fontSize: '12px'
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center',
          fontSize: '14px',
          fontWeight: 600
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 3,
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              height: 300
            },
            xaxis: {
              labels: {
                rotate: -90
              }
            }
          }
        }]
      }
    };
  },

  computed: {
    filteredData() {
      let filtered = [...this.personnel_etcs];
      
      // Apply search filter
      if (this.filters.searchText) {
        const searchLower = this.filters.searchText.toLowerCase();
        filtered = filtered.filter(item => 
          item.subject_typeName.toLowerCase().includes(searchLower) ||
          item.id_type_course.toString().includes(searchLower)
        );
      }
      
      // Apply minimum personnel filter
      if (this.filters.minPersonnel !== null) {
        filtered = filtered.filter(item => 
          parseInt(item.countPersonSubjectTeach) >= this.filters.minPersonnel
        );
      }
      
      // Apply sorting
      const [sortField, sortOrder] = this.filters.sortBy.split("_");
      filtered.sort((a, b) => {
        let aVal, bVal;
        
        switch (sortField) {
          case "total":
            aVal = parseInt(a.countPersonSubjectTeach);
            bVal = parseInt(b.countPersonSubjectTeach);
            break;
          case "gov":
            aVal = parseInt(a.countPersonSubjectTeachGov);
            bVal = parseInt(b.countPersonSubjectTeachGov);
            break;
          case "temp":
            aVal = parseInt(a.countPersonSubjectTeachTem);
            bVal = parseInt(b.countPersonSubjectTeachTem);
            break;
          case "name":
            aVal = a.subject_typeName;
            bVal = b.subject_typeName;
            break;
          default:
            aVal = parseInt(a.countPersonSubjectTeach);
            bVal = parseInt(b.countPersonSubjectTeach);
        }
        
        if (sortOrder === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
      
      return filtered;
    },
    
    currentChartType() {
      return this.filters.chartType;
    },
    
    chartOptions() {
      return {
        ...this.baseChartOptions,
        chart: {
          ...this.baseChartOptions.chart,
          type: this.currentChartType
        },
        xaxis: {
          ...this.baseChartOptions.xaxis,
          categories: this.filteredData.map(item => item.subject_typeName)
        }
      };
    },
    
    hasActiveFilters() {
      return this.filters.searchText || 
             this.filters.minPersonnel !== null || 
             this.filters.chartType !== "bar" || 
             this.filters.sortBy !== "total_desc";
    },
    
    activeFiltersCount() {
      let count = 0;
      if (this.filters.searchText) count++;
      if (this.filters.minPersonnel !== null) count++;
      if (this.filters.chartType !== "bar") count++;
      if (this.filters.sortBy !== "total_desc") count++;
      return count;
    },

    summaryCards() {
      const data = this.filteredData;
      const totalTeachers = data.reduce((sum, item) => 
        sum + parseInt(item.countPersonSubjectTeach || 0), 0);
      const totalGovEmployees = data.reduce((sum, item) => 
        sum + parseInt(item.countPersonSubjectTeachGov || 0), 0);
      const totalTempTeachers = data.reduce((sum, item) => 
        sum + parseInt(item.countPersonSubjectTeachTem || 0), 0);
      
      const grandTotal = totalTeachers + totalGovEmployees + totalTempTeachers;

      return [
        {
          title: 'ข้าราชการครู',
          value: totalTeachers,
          percentage: grandTotal ? ((totalTeachers / grandTotal) * 100).toFixed(1) : 0,
          color: 'success',
          icon: 'mdi-account-tie',
          trend: '+5%',
          trendColor: 'success',
          trendIcon: 'mdi-trending-up'
        },
        {
          title: 'พนักงานราชการ',
          value: totalGovEmployees,
          percentage: grandTotal ? ((totalGovEmployees / grandTotal) * 100).toFixed(1) : 0,
          color: 'info',
          icon: 'mdi-account-group',
          trend: '+2%',
          trendColor: 'success',
          trendIcon: 'mdi-trending-up'
        },
        {
          title: 'ครูอัตราจ้าง',
          value: totalTempTeachers,
          percentage: grandTotal ? ((totalTempTeachers / grandTotal) * 100).toFixed(1) : 0,
          color: 'warning',
          icon: 'mdi-account-clock',
          trend: '-1%',
          trendColor: 'error',
          trendIcon: 'mdi-trending-down'
        }
      ];
    },
    
    statistics() {
      const data = this.filteredData;
      const totalSubjects = data.length;
      const totalPersonnel = data.reduce((sum, item) => 
        sum + parseInt(item.countPersonSubjectTeach || 0), 0);
      const avgPerSubject = totalSubjects ? Math.round(totalPersonnel / totalSubjects) : 0;
      
      let maxSubject = { name: "ไม่มีข้อมูล", value: 0 };
      let minSubject = { name: "ไม่มีข้อมูล", value: 0 };
      
      if (data.length > 0) {
        const maxItem = data.reduce((max, item) => 
          parseInt(item.countPersonSubjectTeach) > parseInt(max.countPersonSubjectTeach) ? item : max
        );
        const minItem = data.reduce((min, item) => 
          parseInt(item.countPersonSubjectTeach) < parseInt(min.countPersonSubjectTeach) ? item : min
        );
        
        maxSubject = { 
          name: maxItem.subject_typeName, 
          value: parseInt(maxItem.countPersonSubjectTeach) 
        };
        minSubject = { 
          name: minItem.subject_typeName, 
          value: parseInt(minItem.countPersonSubjectTeach) 
        };
      }
      
      return {
        totalSubjects,
        avgPerSubject,
        maxSubject,
        minSubject
      };
    },

    pages() {
      if (
        this.pagination.rowsPerPage == null ||
        this.pagination.totalItems == null
      )
        return 0;
      return Math.ceil(
        this.pagination.totalItems / this.pagination.rowsPerPage
      );
    },

    date_today_log() {
      let today = new Date();
      let dd = String(today.getDate()).padStart(2, "0");
      let mm = String(today.getMonth() + 1).padStart(2, "0");
      let yyyy = today.getFullYear() + 543;
      let time =
        today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
      today = dd + "/" + mm + "/" + yyyy + "/" + time;
      return today;
    }
  },

  watch: {
    filteredData: {
      handler() {
        this.updateChartData();
      },
      deep: true
    },
    
    chartViewMode() {
      this.updateChartData();
    }
  },

  created() {
    this.personnel_etcQueryAll();
  },

  methods: {
    async personnel_etcQueryAll() {
      this.loading = true;
      
      try {
        let result = await this.$http.post("personnel_etc.php", {
          ApiKey: this.ApiKey,
          subjectCount: "Ok"
        });
        
        let datafilter = result.data;
        this.personnel_etcs = datafilter.filter(value => 
          value.countPersonSubjectTeach != '0' || 
          value.countPersonSubjectTeachGov != '0' || 
          value.countPersonSubjectTeachTem != '0'
        );

        this.updateChartData();
        
      } catch (error) {
        console.error('Error fetching data:', error);
        if (this.$toast) {
          this.$toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
        }
      } finally {
        this.loading = false;
      }
    },

    updateChartData() {
      const data = this.filteredData;
      let labelText = [];
      let valuesst1 = [];
      let valuesst2 = [];
      let valuesst3 = [];
      
      data.forEach((value) => {
        labelText.push(value.subject_typeName);
        valuesst1.push(parseInt(value.countPersonSubjectTeach || 0));
        valuesst2.push(parseInt(value.countPersonSubjectTeachGov || 0));
        valuesst3.push(parseInt(value.countPersonSubjectTeachTem || 0));
      });

      this.series = [
        {
          name: 'ข้าราชการครู',
          data: valuesst1,
        },
        {
          name: 'พนักงานราชการ',
          data: valuesst2,
        },
        {
          name: 'ครูอัตราจ้าง',
          data: valuesst3,
        }
      ];
    },
                applyFilters() {
      // Filters are automatically applied through computed properties
      this.$nextTick(() => {
        this.updateChartData();
      });
    },
    
    resetFilters() {
      this.filters = {
        searchText: "",
        minPersonnel: null,
        chartType: "bar",
        sortBy: "total_desc"
      };
      this.search = "";
      this.chartViewMode = "count";
    },
    
    refreshData() {
      this.personnel_etcQueryAll();
    },
    
    getChipColor(value, type) {
      const val = parseInt(value);
      switch (type) {
        case 'total':
          if (val >= 50) return 'success';
          if (val >= 20) return 'warning';
          return 'error';
        case 'gov':
          if (val >= 30) return 'info';
          if (val >= 10) return 'primary';
          return 'secondary';
        case 'temp':
          if (val >= 20) return 'warning';
          if (val >= 10) return 'orange';
          return 'deep-orange';
        default:
          return 'primary';
      }
    },
    
    viewDetails(item) {
      // Handle view details action
      console.log('View details for:', item);
      // You can add a dialog or navigate to detail page
    },
    
    exportData() {
      try {
        // Prepare data for export
        const exportData = this.filteredData.map(item => ({
          'รหัสประเภทวิชา': item.id_type_course,
          'ประเภทวิชา': item.subject_typeName,
          'ข้าราชการครู': item.countPersonSubjectTeach,
          'พนักงานราชการ': item.countPersonSubjectTeachGov,
          'ครูอัตราจ้าง': item.countPersonSubjectTeachTem
        }));

        // Create CSV content
        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map(row => 
            headers.map(header => `"${row[header]}"`).join(',')
          )
        ].join('\n');

        // Create and download file
        const blob = new Blob(['\ufeff' + csvContent], { 
          type: 'text/csv;charset=utf-8;' 
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `personnel_report_${this.moment().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        if (this.$toast) {
          this.$toast.success('ส่งออกข้อมูลเรียบร้อยแล้ว');
        }
      } catch (error) {
        console.error('Export error:', error);
        if (this.$toast) {
          this.$toast.error('เกิดข้อผิดพลาดในการส่งออกข้อมูล');
        }
      }
    }
  }
};
</script>

<style scoped>
/* Filter Panel */
.filter-panel {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px !important;
  margin-bottom: 1rem;
}

.filter-panel .v-expansion-panel-header {
  padding: 16px 24px;
  border-radius: 12px !important;
}

.filter-field {
  transition: all 0.3s ease;
}

.filter-field:hover {
  transform: translateY(-2px);
}

/* Summary Cards */
.summary-card {
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.summary-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  pointer-events: none;
}

.summary-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
}

.trend-chip {
  background: rgba(255, 255, 255, 0.2) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

/* Chart Section */
.chart-card {
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98) !important;
}

.chart-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border-radius: 16px 16px 0 0 !important;
}

.chart-toggle {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
}

.chart-container {
  position: relative;
  min-height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-loading, .no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 450px;
  color: #666;
}

/* Table Section */
.table-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.table-header {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white !important;
}

.export-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: white !important;
}

.export-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.search-field, .items-per-page-select {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.search-field:hover, .items-per-page-select:hover {
  transform: translateY(-2px);
}

.enhanced-table {
  border-radius: 12px !important;
  overflow: hidden;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 12px !important;
}

.enhanced-table >>> thead th {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  color: #2d3748 !important;
  font-weight: 700 !important;
  border-bottom: 2px solid #cbd5e0 !important;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.05em;
}

.enhanced-table >>> tbody tr {
  transition: all 0.2s ease;
}

.enhanced-table >>> tbody tr:hover {
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%) !important;
  transform: translateX(4px);
}

.chip-animate {
  transition: all 0.3s ease;
}

.chip-animate:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Statistics Summary */
.stats-summary {
  border-radius: 16px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stats-header {
  color: white !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.stat-item {
  text-align: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-4px);
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .summary-card {
    margin-bottom: 1rem;
  }
  
  .chart-container {
    min-height: 350px;
  }
  
  .stat-item {
    margin-bottom: 1rem;
  }
  
  .enhanced-table >>> th,
  .enhanced-table >>> td {
    font-size: 0.8rem;
    padding: 8px 4px;
  }
}

@media (max-width: 480px) {
  .chart-container {
    min-height: 300px;
  }
  
  .stat-value {
    font-size: 1.25rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.summary-card:nth-child(1) {
  animation: slideInLeft 0.6s ease-out;
  animation-delay: 0.1s;
}

.summary-card:nth-child(2) {
  animation: fadeInUp 0.6s ease-out;
  animation-delay: 0.2s;
}

.summary-card:nth-child(3) {
  animation: slideInRight 0.6s ease-out;
  animation-delay: 0.3s;
}

.chart-card {
  animation: fadeInUp 0.6s ease-out;
  animation-delay: 0.4s;
}

.table-card {
  animation: fadeInUp 0.6s ease-out;
  animation-delay: 0.5s;
}

.stats-summary {
  animation: fadeInUp 0.6s ease-out;
  animation-delay: 0.6s;
}

/* Loading Animation */
.loading-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

/* Hover Effects */
.summary-card:hover .v-icon,
.chart-card:hover .v-icon,
.table-card:hover .v-icon,
.stats-summary:hover .v-icon {
  transform: scale(1.1);
  transition: transform 0.3s ease;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
}

/* Print Styles */
@media print {
  .filter-panel,
  .chart-toggle,
  .export-btn {
    display: none !important;
  }
  
  .summary-card,
  .chart-card,
  .table-card,
  .stats-summary {
    background: white !important;
    color: black !important;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }
  
  .chart-container {
    height: 300px !important;
  }
}
</style>
    
    